
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { TrendingUp, Users, BookOpen, Award, Clock, Target } from 'lucide-react';
import { useStaffCapabilities } from '@/hooks/auth/useStaffCapabilities';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

const StaffUserAnalytics: React.FC = () => {
  const { hasStaffAccess } = useStaffCapabilities();
  const { toast } = useToast();
  const [timeFrame, setTimeFrame] = useState('30');
  const [loading, setLoading] = useState(true);
  const [analyticsData, setAnalyticsData] = useState<any>({
    userGrowth: [],
    courseDistribution: [],
    completionStats: {
      totalAssignments: 0,
      completed: 0,
      inProgress: 0,
      notStarted: 0,
      averageTime: 0,
      successRate: 0
    }
  });

  const loadAnalytics = async () => {
    if (!hasStaffAccess) return;
    
    try {
      setLoading(true);
      const { data, error } = await supabase.rpc('get_staff_user_growth_analytics', {
        days_back: parseInt(timeFrame) * 6 // Convert to approximately the right timeframe
      });
      
      if (error) throw error;
      
      const analytics = data as any;
      if (analytics?.error) {
        throw new Error(analytics.error);
      }

      setAnalyticsData({
        userGrowth: analytics.userGrowth || [],
        courseDistribution: analytics.courseDistribution || [],
        completionStats: analytics.completionStats || {
          totalAssignments: 0,
          completed: 0,
          inProgress: 0,
          notStarted: 0,
          averageTime: 0,
          successRate: 0
        }
      });
    } catch (error) {
      console.error('Error loading analytics:', error);
      toast({
        title: "Error",
        description: "Failed to load analytics data",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAnalytics();
  }, [timeFrame, hasStaffAccess]);

  if (!hasStaffAccess) {
    return (
      <Card>
        <CardContent className="p-8 text-center">
          <h3 className="text-lg font-medium mb-2">Access Denied</h3>
          <p className="text-muted-foreground">You need Staff access to view analytics.</p>
        </CardContent>
      </Card>
    );
  }

  if (loading) {
    return (
      <div className="space-y-6">
        {[1, 2, 3].map(i => (
          <Card key={i} className="animate-pulse">
            <CardContent className="p-6">
              <div className="h-40 bg-muted rounded"></div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">User Learning Analytics</h2>
          <p className="text-muted-foreground">Track user progress and course performance</p>
        </div>
        <Select value={timeFrame} onValueChange={setTimeFrame}>
          <SelectTrigger className="w-48">
            <SelectValue placeholder="Select timeframe" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="7">Last 7 days</SelectItem>
            <SelectItem value="30">Last 30 days</SelectItem>
            <SelectItem value="90">Last 3 months</SelectItem>
            <SelectItem value="365">Last year</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Total Assignments</p>
                <p className="text-2xl font-bold">{analyticsData.completionStats.totalAssignments}</p>
              </div>
              <BookOpen className="h-8 w-8 text-blue-600" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Completion Rate</p>
                <p className="text-2xl font-bold">{analyticsData.completionStats.successRate}%</p>
              </div>
              <Target className="h-8 w-8 text-green-600" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Avg. Completion</p>
                <p className="text-2xl font-bold">{analyticsData.completionStats.averageTime}d</p>
              </div>
              <Clock className="h-8 w-8 text-orange-600" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Certificates</p>
                <p className="text-2xl font-bold">{analyticsData.completionStats.completed}</p>
              </div>
              <Award className="h-8 w-8 text-purple-600" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* User Growth Chart */}
        <Card>
          <CardHeader>
            <CardTitle>User Progress Trends</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={analyticsData.userGrowth}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="users" fill="#3b82f6" name="Enrolled" />
                <Bar dataKey="completed" fill="#10b981" name="Completed" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Course Distribution */}
        <Card>
          <CardHeader>
            <CardTitle>Course Assignment Distribution</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={analyticsData.courseDistribution}
                  cx="50%"
                  cy="50%"
                  outerRadius={100}
                  fill="#8884d8"
                  dataKey="value"
                  label={({ name, value }) => `${name}: ${value}%`}
                >
                  {analyticsData.courseDistribution.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* Assignment Status Breakdown */}
      <Card>
        <CardHeader>
          <CardTitle>Assignment Status Overview</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="text-center p-4 border rounded-lg">
              <div className="text-2xl font-bold text-green-600">{analyticsData.completionStats.completed}</div>
              <div className="text-sm text-muted-foreground">Completed</div>
              <Badge variant="default" className="mt-2">
                {Math.round((analyticsData.completionStats.completed / analyticsData.completionStats.totalAssignments) * 100)}%
              </Badge>
            </div>
            <div className="text-center p-4 border rounded-lg">
              <div className="text-2xl font-bold text-blue-600">{analyticsData.completionStats.inProgress}</div>
              <div className="text-sm text-muted-foreground">In Progress</div>
              <Badge variant="secondary" className="mt-2">
                {Math.round((analyticsData.completionStats.inProgress / analyticsData.completionStats.totalAssignments) * 100)}%
              </Badge>
            </div>
            <div className="text-center p-4 border rounded-lg">
              <div className="text-2xl font-bold text-orange-600">{analyticsData.completionStats.notStarted}</div>
              <div className="text-sm text-muted-foreground">Not Started</div>
              <Badge variant="outline" className="mt-2">
                {Math.round((analyticsData.completionStats.notStarted / analyticsData.completionStats.totalAssignments) * 100)}%
              </Badge>
            </div>
            <div className="text-center p-4 border rounded-lg">
              <div className="text-2xl font-bold text-purple-600">{analyticsData.completionStats.totalAssignments}</div>
              <div className="text-sm text-muted-foreground">Total</div>
              <Badge variant="secondary" className="mt-2">100%</Badge>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default StaffUserAnalytics;
